import { useCallback, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useFormContext } from 'react-hook-form';
import { useEventMapContext } from 'src/contexts/EventMapContext/EventMapContext';
import { SelectedSectionSettingsWithOptionals } from 'src/utils/types/selectedSectionSettingsWithOptionals';
import {
  ListingDetailsAutoPricingSectionUpdatesV2,
  SectionFilterMode,
} from 'src/WebApiController';

import { AutoPricingSettingsInput } from '../../BulkEditAutoPricingSettingsDialog.types';
import { useAutoPricingSettings } from '../../useBulkAutoPricingSettings';
import { SectionSelectionMode } from './BulkEditAutoPricingSettingsSectionSelector.types';

export const useBulkEditAutoPricingSettingsSectionSelector = (
  origin: Pick<AutoPricingSettingsInput, 'compListingSelectedSectionSettings'>
) => {
  const { watch, setValue, formState } =
    useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();

  const { deriveCompsFromVenueZoneConfig } = watch();

  const {
    compListingSelectedSectionSettings,
    includeExistingSelectedSections,
    onCompListingChange,
  } = useAutoPricingSettings();

  const { event } = useEventMapContext();

  const sectionSelectionMode = useMemo(() => {
    if (deriveCompsFromVenueZoneConfig) {
      return SectionSelectionMode.ZoneMap;
    }

    if (
      compListingSelectedSectionSettings?.value?.sectionFilterMode ==
      SectionFilterMode.SameSection
    ) {
      return SectionSelectionMode.SameSection;
    }

    return SectionSelectionMode.CustomSections;
  }, [
    compListingSelectedSectionSettings?.value?.sectionFilterMode,
    deriveCompsFromVenueZoneConfig,
  ]);

  const availableSectionSelectionModes = useMemo(() => {
    const modes = new Set<SectionSelectionMode>();
    if (sectionSelectionMode !== SectionSelectionMode.SameSection) {
      modes.add(SectionSelectionMode.SameSection);
    }

    if (event) {
      if (sectionSelectionMode !== SectionSelectionMode.CustomSections) {
        modes.add(SectionSelectionMode.CustomSections);
      }

      if (sectionSelectionMode !== SectionSelectionMode.ZoneMap) {
        modes.add(SectionSelectionMode.ZoneMap);
      }
    }

    return modes;
  }, [event, sectionSelectionMode]);

  const setSectionSelectionMode = useCallback(
    (newMode: SectionSelectionMode) => {
      const newDeriveCompsFromVenueZoneConfig =
        newMode === SectionSelectionMode.ZoneMap;
      const newSectionFilterMode =
        newMode === SectionSelectionMode.SameSection
          ? SectionFilterMode.SameSection
          : null;
      let newSectionIdFilter: number[] = [];
      let newRowIdFilter: number[] = [];
      let newSectionRowIdFilter = {};
      let newIncludeExistingSelectedSections = false;
      if (newMode === SectionSelectionMode.CustomSections) {
        newSectionIdFilter =
          origin.compListingSelectedSectionSettings?.value?.sectionIdFilter ??
          [];
        newRowIdFilter =
          origin.compListingSelectedSectionSettings?.value?.rowIdFilter ?? [];
        newSectionRowIdFilter =
          origin.compListingSelectedSectionSettings?.value
            ?.sectionRowIdFilter ?? {};
        newIncludeExistingSelectedSections = true;
      }

      const newCompListingSelectedSectionSettings: SelectedSectionSettingsWithOptionals =
        {
          ...(origin.compListingSelectedSectionSettings?.value ?? {}),
          sectionIdFilter: newSectionIdFilter,
          rowIdFilter: newRowIdFilter,
          sectionRowIdFilter: newSectionRowIdFilter,
          sectionFilterMode: newSectionFilterMode,
          sectionIdFilterFromZoneMap: false,
          includeBetterZones: false,
        };

      setValue(
        'includeExistingSelectedSections',
        newIncludeExistingSelectedSections
      );
      setValue(
        'deriveCompsFromVenueZoneConfig',
        newDeriveCompsFromVenueZoneConfig
      );
      onCompListingChange({
        compListingSelectedSectionSettingsNew:
          newCompListingSelectedSectionSettings,
      });
    },
    [
      onCompListingChange,
      origin.compListingSelectedSectionSettings?.value,
      setValue,
    ]
  );

  const selectedSectionsHasChange = useMemo(
    () =>
      deriveCompsFromVenueZoneConfig ||
      !isEqual(
        origin.compListingSelectedSectionSettings?.value,
        compListingSelectedSectionSettings?.value
      ) ||
      (origin.compListingSelectedSectionSettings?.hasConflict &&
        !includeExistingSelectedSections),
    [
      compListingSelectedSectionSettings?.value,
      deriveCompsFromVenueZoneConfig,
      includeExistingSelectedSections,
      origin.compListingSelectedSectionSettings?.hasConflict,
      origin.compListingSelectedSectionSettings?.value,
    ]
  );

  const onResetSelectedSections = useCallback(() => {
    onCompListingChange({
      compListingSelectedSectionSettingsNew:
        origin.compListingSelectedSectionSettings?.value,
    });

    setValue('deriveCompsFromVenueZoneConfig', false);
    setValue('includeBetterZones', false);
    setValue(
      'includeExistingSelectedSections',
      formState.defaultValues?.includeExistingSelectedSections ?? null
    );
  }, [
    formState.defaultValues?.includeExistingSelectedSections,
    onCompListingChange,
    origin.compListingSelectedSectionSettings?.value,
    setValue,
  ]);

  return {
    sectionSelectionMode,
    setSectionSelectionMode,
    availableSectionSelectionModes,
    selectedSectionsHasChange,
    onResetSelectedSections,
  };
};
